import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from './config';

const storage = getStorage();

// Default avatar URL - using a reliable avatar service
export const DEFAULT_AVATAR_URL = 'https://ui-avatars.com/api/?name=User&background=ef4444&color=fff&size=200';

// Generate default avatar URL with user's initials
export const getDefaultAvatar = (name = 'User') => {
  const initials = name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
  
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=ef4444&color=fff&size=200&bold=true`;
};

// Upload profile picture
export const uploadProfilePicture = async (userId, file) => {
  try {
    // Validate file
    if (!file) {
      throw new Error('No file provided');
    }

    // Check file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload a JPEG, PNG, WebP, or GIF image.');
    }

    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      throw new Error('File size too large. Maximum size is 5MB.');
    }

    // Create unique filename
    const timestamp = Date.now();
    const filename = `${userId}_${timestamp}_${file.name}`;
    const storageRef = ref(storage, `profile-pictures/${filename}`);

    // Upload file
    await uploadBytes(storageRef, file);

    // Get download URL
    const downloadURL = await getDownloadURL(storageRef);

    // Update user document in Firestore
    await updateDoc(doc(db, 'users', userId), {
      avatar: downloadURL
    });

    return { success: true, url: downloadURL };
  } catch (error) {
    console.error('Error uploading profile picture:', error);
    return { success: false, error: error.message };
  }
};

// Delete profile picture (optional - for cleanup)
export const deleteProfilePicture = async (userId, photoURL) => {
  try {
    // Don't delete if it's the default avatar
    if (photoURL.includes('ui-avatars.com')) {
      return { success: true };
    }

    // Extract filename from URL
    const filename = photoURL.split('/').pop().split('?')[0];
    const storageRef = ref(storage, `profile-pictures/${decodeURIComponent(filename)}`);

    // Delete file
    await deleteObject(storageRef);

    // Reset to default avatar
    const defaultAvatar = getDefaultAvatar('User');
    await updateDoc(doc(db, 'users', userId), {
      avatar: defaultAvatar
    });

    return { success: true };
  } catch (error) {
    console.error('Error deleting profile picture:', error);
    return { success: false, error: error.message };
  }
};

// Reset all users to default avatars (admin function)
export const resetAllAvatarsToDefault = async () => {
  try {
    const { collection, getDocs } = await import('firebase/firestore');
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    
    const updates = [];
    snapshot.forEach((doc) => {
      const userData = doc.data();
      const defaultAvatar = getDefaultAvatar(userData.name || 'User');
      updates.push(
        updateDoc(doc.ref, {
          avatar: defaultAvatar
        })
      );
    });

    await Promise.all(updates);
    
    return { success: true, count: updates.length };
  } catch (error) {
    console.error('Error resetting avatars:', error);
    return { success: false, error: error.message };
  }
};
